<script lang="ts" setup>
import { computed } from "vue";
import { useRouter } from "vue-router";
import { SysOrgRoleName } from "~/api";
import { applicationRouteMap } from "~/router/mainRouter";
import HelpAndSupportMenuSection from "~/components/HelpAndSupportMenuSection.vue";
import { useMyInfoStore } from "~/stores/MyInfoStore";

const router = useRouter();
const myInfoStore = useMyInfoStore();
myInfoStore.load();

/** Starting section of the route we are currently navigated to. Ex. "/forms" */
const currentRouteStart = computed<string>(
  () => "/" + router.currentRoute.value.path.split("/")[1],
);

const filterRoutes = (r: any) => {
  if (r.meta) {
    const meta = r.meta;
    const roles = meta.roles as SysOrgRoleName[];
    if (roles?.length) {
      return roles.some((role: SysOrgRoleName) =>
        myInfoStore.currentSysOrg?.roles.includes(role),
      );
    }
    return false;
  }
  return true;
};

// Extract dashboard routes from HMB section
const dashboardRoutes = [applicationRouteMap.HMB.children.Dashboard];

// Extract admin routes from HMB section
const adminRoutes = Object.values(
  applicationRouteMap.HMB.children.Admin.children,
).filter((r) => r.path.indexOf("/") < 0);

// Extract admin routes from HMB section
const sysAdminRoutes = Object.values(
  applicationRouteMap.HMB.children.SysAdmin.children,
)
  .filter((r) => r.path.indexOf("/") < 0)
  .filter((r) => {
    const hasDesignFormsRole = myInfoStore.hasSetupBasicRole;

    if (
      !hasDesignFormsRole &&
      !myInfoStore.isSuperAdmin &&
      (r.name ===
        applicationRouteMap.HMB.children.SysAdmin.children.ClientActivity
          .name ||
        r.name === applicationRouteMap.HMB.children.SysAdmin.children.Logs.name)
    ) {
      return false;
    }

    if (
      !hasDesignFormsRole &&
      (r.name ===
        applicationRouteMap.HMB.children.SysAdmin.children.DesignForms.name ||
        r.name ===
          applicationRouteMap.HMB.children.SysAdmin.children.BulkUpload.name ||
        r.name ===
          applicationRouteMap.HMB.children.SysAdmin.children.TreeDefinitions
            .name ||
        r.name ===
          applicationRouteMap.HMB.children.SysAdmin.children
            .SectorUnitTypeMappings.name)
    ) {
      return false;
    }

    return true;
  });

// Extract migration routes — only visible to SetupBasic role
const migrationRoutes = myInfoStore.hasSetupBasicRole
  ? Object.values(applicationRouteMap.HMB.children.Migrations.children).filter(
      (r) => r.path.indexOf("/") < 0,
    )
  : [];

// Contact Management routes (dynamically populated CRM entities + workflows)
const contactManagementRoutes = computed(() =>
  Object.values(
    applicationRouteMap.HMB.children.ContactManagement.children,
  ).filter(filterRoutes),
);

// get Process routes
const processRoutes = Object.values(
  applicationRouteMap.HMB.children.Process.children,
).filter((r) => r.path.indexOf("/") < 0);

const isCurrentRoute = (path: string): boolean =>
  currentRouteStart.value === "/"
    ? false
    : path.startsWith(currentRouteStart.value);
</script>

<template>
  <div class="hmbSidebar">
    <router-link
      v-for="route in dashboardRoutes"
      :key="route.path"
      :to="`${applicationRouteMap.HMB.path}/${route.path}`"
      :class="{ 'router-link-exact-active': isCurrentRoute(route.path) }"
    >
      {{ route.name }}
    </router-link>

    <div class="header">Process</div>
    <router-link
      v-for="route in processRoutes"
      :key="route.path"
      :to="`${applicationRouteMap.HMB.path}/process/${route.path}`"
      :class="{ 'router-link-exact-active': isCurrentRoute(route.path) }"
      class="routes"
    >
      {{ route.name }}
    </router-link>

    <div v-if="contactManagementRoutes.length" class="header">
      Contact Management
    </div>
    <router-link
      v-for="route in contactManagementRoutes"
      :key="route.path"
      :to="`${applicationRouteMap.HMB.path}/${route.path}`"
      :class="{ 'router-link-exact-active': isCurrentRoute(route.path) }"
      class="routes"
    >
      {{ route.meta?.display }}
    </router-link>

    <HelpAndSupportMenuSection
      :support-tickets-path="`${applicationRouteMap.HMB.path}/support-tickets`"
      :help-faq-path="`${applicationRouteMap.HMB.path}/help-faq`"
      :is-current-route="isCurrentRoute"
      :wrap-in-section="false"
    />

    <div class="header">Admin</div>
    <router-link
      v-for="route in adminRoutes"
      :key="route.path"
      :to="`${applicationRouteMap.HMB.path}/admin/${route.path}`"
      :class="{ 'router-link-exact-active': isCurrentRoute(route.path) }"
      class="routes"
    >
      {{ route.name }}
    </router-link>

    <div class="header">System Admin</div>
    <router-link
      v-for="route in sysAdminRoutes"
      :key="route.path"
      :to="`${applicationRouteMap.HMB.path}/sysadmin/${route.path}`"
      :class="{ 'router-link-exact-active': isCurrentRoute(route.path) }"
      class="routes"
    >
      {{ route.name }}
    </router-link>

    <template v-if="migrationRoutes.length">
      <div class="header">Migrations</div>
      <router-link
        v-for="route in migrationRoutes"
        :key="route.path"
        :to="`${applicationRouteMap.HMB.path}/migrations/${route.path}`"
        :class="{ 'router-link-exact-active': isCurrentRoute(route.path) }"
        class="routes"
      >
        {{ route.name }}
      </router-link>
    </template>

    <el-button
      v-if="myInfoStore.isSuperAdmin"
      type="primary"
      size="small"
      @click="router.push('/compliance/')"
    >
      SA &rarr; Base Framework Menu
    </el-button>
  </div>
</template>

<style lang="less">
.hmbSidebar {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: min-content;
  overflow-y: auto;
  padding: 1em 1.5em 3em 0.5em;
  font-size: 1.1rem;
  background-color: var(--el-color-primary-dark-3);
  > a {
    text-wrap: nowrap;
    color: var(--el-color-white);
    text-decoration: none;
    padding-left: 0.8rem;
  }
  > a.router-link-exact-active {
    color: #ffd04b;
  }

  .header {
    font-weight: 600;
    color: var(--el-color-primary-light-8);
    cursor: default;
    margin-top: 1.5em;
  }

  .routes {
    padding-left: 0.8rem;
  }
}
</style>
